import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { Plus, Edit, Building2, ChevronRight, ChevronDown, MapPin, Users } from 'lucide-react';
import { RecordActions } from '../../../components/common/RecordActions';
import { RecordViewDialog } from '../../../components/common/RecordViewDialog';
import { useAuth } from '../../../context/AuthContext';
import { omAPI, hcmAPI, companyAPI, controllingAPI } from '../../../services/api';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { Card } from '../../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { toast } from 'sonner';

const OrgUnits = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('orgunits');
  const [viewItem, setViewItem] = useState(null);
  const navigate = useNavigate();
  
  const [orgUnits, setOrgUnits] = useState([]);
  const [personnelAreas, setPersonnelAreas] = useState([]);
  const [controllingAreas, setControllingAreas] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formResetCounter, setFormResetCounter] = useState(0);
  const [expandedNodes, setExpandedNodes] = useState({});
  const [viewMode, setViewMode] = useState('tree'); // 'tree' or 'list'
  
  // Form state
  const [selectedParent, setSelectedParent] = useState('');
  const [selectedPersonnelArea, setSelectedPersonnelArea] = useState('');
  const [selectedControllingArea, setSelectedControllingArea] = useState('');
  const [selectedCostCenter, setSelectedCostCenter] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('Active');

  useEffect(() => {
    loadData();
    loadMasterData();
  }, []);

  const loadMasterData = async () => {
    try {
      const [paRes, caRes] = await Promise.all([
        hcmAPI.getPersonnelAreas(),
        controllingAPI.getControllingAreas()
      ]);
      setPersonnelAreas(paRes.data || []);
      setControllingAreas(caRes.data || []);
    } catch (error) {
      console.error('Failed to load master data:', error);
    }
  };

  const loadData = async () => {
    try {
      const res = await omAPI.getOrgUnits();
      setOrgUnits(res.data || []);
    } catch (error) {
      console.error('Failed to load org units:', error);
      toast.error('Failed to load organizational units');
    }
  };

  const loadCostCenters = async (controllingAreaId) => {
    if (!controllingAreaId) {
      setCostCenters([]);
      return;
    }
    try {
      const res = await controllingAPI.getCostCenters(controllingAreaId);
      setCostCenters(res.data || []);
    } catch (error) {
      console.error('Failed to load cost centers:', error);
      setCostCenters([]);
    }
  };

  const handleControllingAreaChange = (value) => {
    setSelectedControllingArea(value);
    setSelectedCostCenter('');
    loadCostCenters(value);
  };

  const resetFormState = () => {
    setSelectedParent('');
    setSelectedPersonnelArea('');
    setSelectedControllingArea('');
    setSelectedCostCenter('');
    setSelectedStatus('Active');
    setCostCenters([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    // Clean up empty strings
    Object.keys(data).forEach(key => {
      if (data[key] === '' || data[key] === 'none') {
        delete data[key];
      }
    });

    try {
      if (editingItem) {
        await omAPI.updateOrgUnit(editingItem.org_unit_id, data);
        toast.success('Organizational Unit updated successfully');
      } else {
        await omAPI.createOrgUnit(data);
        toast.success('Organizational Unit created successfully');
      }
      
      setIsDialogOpen(false);
      setEditingItem(null);
      resetFormState();
      setFormResetCounter(prev => prev + 1);
      loadData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const firstError = detail[0];
        const fieldName = firstError?.loc?.slice(-1)[0] || 'field';
        toast.error(`${fieldName}: ${firstError?.msg || 'Validation error'}`);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Operation failed');
      }
    }
  };

  const handleEdit = async (item) => {
    setEditingItem(item);
    setSelectedParent(item.parent_org_unit_id || '');
    setSelectedPersonnelArea(item.personnel_area_id || '');
    setSelectedControllingArea(item.controlling_area_id || '');
    setSelectedStatus(item.status || 'Active');
    
    if (item.controlling_area_id) {
      await loadCostCenters(item.controlling_area_id);
      setSelectedCostCenter(item.cost_center_id || '');
    }
    
    setIsDialogOpen(true);
    setFormResetCounter(prev => prev + 1);
  };

  const toggleNode = (orgUnitId) => {
    setExpandedNodes(prev => ({
      ...prev,
      [orgUnitId]: !prev[orgUnitId]
    }));
  };

  // Build tree structure
  const buildTree = useCallback(() => {
    const map = {};
    const roots = [];
    
    // Create map
    orgUnits.forEach(ou => {
      map[ou.org_unit_id] = { ...ou, children: [] };
    });
    
    // Build tree
    orgUnits.forEach(ou => {
      if (ou.parent_org_unit_id && map[ou.parent_org_unit_id]) {
        map[ou.parent_org_unit_id].children.push(map[ou.org_unit_id]);
      } else {
        roots.push(map[ou.org_unit_id]);
      }
    });
    
    return roots;
  }, [orgUnits]);

  const renderTreeNode = (node, level = 0) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes[node.org_unit_id];
    
    return (
      <div key={node.org_unit_id}>
        <div 
          className={`flex items-center gap-2 py-2 px-3 hover:bg-gray-50 border-b border-gray-100 ${level > 0 ? 'ml-6' : ''}`}
          style={{ paddingLeft: `${level * 24 + 12}px` }}
        >
          {hasChildren ? (
            <button onClick={() => toggleNode(node.org_unit_id)} className="p-1 hover:bg-gray-200 rounded">
              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          ) : (
            <div className="w-6" />
          )}
          
          <Building2 className="h-4 w-4 text-orange-600" />
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm font-semibold text-gray-800">{node.org_unit_id}</span>
              <span className="text-sm font-medium text-gray-900 truncate">{node.name}</span>
              {node.short_name && <span className="text-xs text-gray-500">({node.short_name})</span>}
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-500">
              {node.location && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {node.location}
                </span>
              )}
              {node.position_count > 0 && (
                <span className="flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {node.position_count} positions
                </span>
              )}
              {node.personnel_area_name && (
                <span>PA: {node.personnel_area_name}</span>
              )}
            </div>
          </div>
          
          <span className={`px-2 py-1 text-xs font-semibold rounded ${
            node.status === 'Active' ? 'bg-green-100 text-green-800' : 
            node.status === 'Planned' ? 'bg-orange-100 text-orange-800' : 'bg-red-100 text-red-800'
          }`}>
            {node.status}
          </span>
          
          <RecordActions doctype="orgunits" onView={() => setViewItem(node)} onEdit={() => handleEdit(node)} />
        </div>
        
        {hasChildren && isExpanded && (
          <div className="border-l-2 border-gray-200 ml-4">
            {node.children.map(child => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const treeRoots = buildTree();

  return (
    <Layout>
      <div className="p-6" data-testid="org-units-page">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <span className="cursor-pointer hover:text-gray-700" onClick={() => navigate('/hcm')}>HCM</span>
            <span>/</span>
            <span className="cursor-pointer hover:text-gray-700" onClick={() => navigate('/hcm/om')}>Org Management</span>
            <span>/</span>
            <span>Org Units</span>
          </div>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Organizational Units</h1>
              <p className="text-gray-600 mt-1">Manage departments, divisions, and locations</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex border rounded-lg overflow-hidden">
                <button 
                  onClick={() => setViewMode('tree')}
                  className={`px-3 py-1.5 text-sm ${viewMode === 'tree' ? 'bg-orange-600 text-white' : 'bg-white text-gray-700'}`}
                >
                  Tree
                </button>
                <button 
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-1.5 text-sm ${viewMode === 'list' ? 'bg-orange-600 text-white' : 'bg-white text-gray-700'}`}
                >
                  List
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Content Card */}
        <Card className="p-0 overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-gray-500" />
              <h2 className="font-semibold text-gray-900">Org Units</h2>
              <span className="text-sm text-gray-500">({orgUnits.length} total)</span>
            </div>
            <Dialog open={isDialogOpen} onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) {
                setEditingItem(null);
                resetFormState();
              }
            }}>
              <DialogTrigger asChild>
                {writable && (<Button size="sm" data-testid="create-btn">
                  <Plus className="h-4 w-4 mr-1" />
                  Create Org Unit
                </Button>)}
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingItem ? 'Edit' : 'Create'} Organizational Unit</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} key={formResetCounter} className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="org_unit_id">Org Unit ID *</Label>
                      <Input id="org_unit_id" name="org_unit_id" placeholder="e.g., DEPT001, HQ01" maxLength={20} defaultValue={editingItem?.org_unit_id || ''} disabled={!!editingItem} required />
                    </div>
                    <div>
                      <Label htmlFor="name">Name *</Label>
                      <Input id="name" name="name" placeholder="e.g., Human Resources" maxLength={100} defaultValue={editingItem?.name || ''} required />
                    </div>
                    <div>
                      <Label htmlFor="short_name">Short Name</Label>
                      <Input id="short_name" name="short_name" placeholder="e.g., HR" maxLength={20} defaultValue={editingItem?.short_name || ''} />
                    </div>
                    <div>
                      <Label htmlFor="parent_org_unit_id">Parent Org Unit</Label>
                      <Select value={selectedParent} onValueChange={setSelectedParent}>
                        <SelectTrigger><SelectValue placeholder="Select Parent (optional)" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">-- No Parent (Root) --</SelectItem>
                          {orgUnits.filter(ou => ou.org_unit_id !== editingItem?.org_unit_id && ou.status === 'Active').map(ou => (
                            <SelectItem key={ou.org_unit_id} value={ou.org_unit_id}>{ou.org_unit_id} - {ou.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <input type="hidden" name="parent_org_unit_id" value={selectedParent === 'none' ? '' : selectedParent} />
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="description">Description</Label>
                      <Input id="description" name="description" placeholder="Detailed description" maxLength={500} defaultValue={editingItem?.description || ''} />
                    </div>
                    <div>
                      <Label htmlFor="personnel_area_id">Personnel Area</Label>
                      <Select value={selectedPersonnelArea} onValueChange={setSelectedPersonnelArea}>
                        <SelectTrigger><SelectValue placeholder="Select Personnel Area" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">-- None --</SelectItem>
                          {personnelAreas.filter(pa => pa.status === 'Active').map(pa => (
                            <SelectItem key={pa.personnel_area_id} value={pa.personnel_area_id}>{pa.personnel_area_id} - {pa.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <input type="hidden" name="personnel_area_id" value={selectedPersonnelArea === 'none' ? '' : selectedPersonnelArea} />
                    </div>
                    <div>
                      <Label htmlFor="location">Location</Label>
                      <Input id="location" name="location" placeholder="e.g., Building A, Floor 3" maxLength={100} defaultValue={editingItem?.location || ''} />
                    </div>
                    <div>
                      <Label htmlFor="controlling_area_id">Controlling Area</Label>
                      <Select value={selectedControllingArea} onValueChange={handleControllingAreaChange}>
                        <SelectTrigger><SelectValue placeholder="Select Controlling Area" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">-- None --</SelectItem>
                          {controllingAreas.filter(ca => ca.status === 'Active').map(ca => (
                            <SelectItem key={ca.controlling_area_id} value={ca.controlling_area_id}>{ca.controlling_area_id} - {ca.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <input type="hidden" name="controlling_area_id" value={selectedControllingArea === 'none' ? '' : selectedControllingArea} />
                    </div>
                    <div>
                      <Label htmlFor="cost_center_id">Cost Center</Label>
                      <Select value={selectedCostCenter} onValueChange={setSelectedCostCenter} disabled={!selectedControllingArea || selectedControllingArea === 'none'}>
                        <SelectTrigger><SelectValue placeholder={selectedControllingArea && selectedControllingArea !== 'none' ? "Select Cost Center" : "Select Controlling Area first"} /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">-- None --</SelectItem>
                          {costCenters.filter(cc => cc.status === 'Active').map(cc => (
                            <SelectItem key={cc.cost_center_id} value={cc.cost_center_id}>{cc.cost_center_id} - {cc.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <input type="hidden" name="cost_center_id" value={selectedCostCenter === 'none' ? '' : selectedCostCenter} />
                    </div>
                    <div>
                      <Label htmlFor="status">Status *</Label>
                      <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                        <SelectTrigger><SelectValue placeholder="Select Status" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Active">Active</SelectItem>
                          <SelectItem value="Planned">Planned</SelectItem>
                          <SelectItem value="Blocked">Blocked</SelectItem>
                        </SelectContent>
                      </Select>
                      <input type="hidden" name="status" value={selectedStatus} />
                    </div>
                    <div>
                      <Label htmlFor="valid_from">Valid From *</Label>
                      <Input type="date" id="valid_from" name="valid_from" defaultValue={editingItem?.valid_from || new Date().toISOString().split('T')[0]} required />
                    </div>
                    <div>
                      <Label htmlFor="valid_to">Valid To</Label>
                      <Input type="date" id="valid_to" name="valid_to" defaultValue={editingItem?.valid_to || ''} />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                    <Button type="submit" data-testid="submit-btn">{editingItem ? 'Update' : 'Create'}</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Tree/List View */}
          <div className="overflow-x-auto">
            {viewMode === 'tree' ? (
              <div className="min-w-[600px]">
                {treeRoots.length > 0 ? (
                  treeRoots.map(root => renderTreeNode(root))
                ) : (
                  <div className="py-8 text-center text-gray-500">
                    No organizational units found. Click "Create Org Unit" to add the first one.
                  </div>
                )}
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">ID</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Name</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Parent</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Personnel Area</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Location</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Positions</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orgUnits.length > 0 ? (
                    orgUnits.map((item) => (
                      <tr key={item.org_unit_id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 font-mono text-sm font-semibold">{item.org_unit_id}</td>
                        <td className="py-3 px-4">
                          <div className="text-sm font-medium">{item.name}</div>
                          {item.short_name && <div className="text-xs text-gray-500">{item.short_name}</div>}
                        </td>
                        <td className="py-3 px-4 text-sm">{item.parent_org_unit_name || '-'}</td>
                        <td className="py-3 px-4 text-sm">{item.personnel_area_name || '-'}</td>
                        <td className="py-3 px-4 text-sm">{item.location || '-'}</td>
                        <td className="py-3 px-4 text-sm">{item.position_count || 0}</td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded ${
                            item.status === 'Active' ? 'bg-green-100 text-green-800' :
                            item.status === 'Planned' ? 'bg-orange-100 text-orange-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {item.status}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <RecordActions doctype="orgunits" onView={() => setViewItem(item)} onEdit={() => handleEdit(item)} />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} className="py-8 text-center text-gray-500">
                        No organizational units found. Click "Create Org Unit" to add one.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </Card>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default OrgUnits;
